import React, { useState, useCallback, useEffect, useRef } from "react";
import "./MathPractice.css";
import { awardToken } from "../../awardtoken";
import { db } from "../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../components/useAuth";

const SOUND_SOURCES = [
  "/sounds/correct.mp3",
  "/sounds/correct.ogg",
  "/sounds/correct.wav",
];

const DIGIT_OPTIONS = [
  { label: "Single-digit (x − x)", value: 1 },
  { label: "Double-digit (xx − xx)", value: 2 },
  { label: "Triple-digit (xxx − xxx)", value: 3 },
];
const SHEET_SIZES = [10, 25, 50];
const TOOL_TYPE = "MathSubtractionPractice";

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const genNum = (d) => (d === 1 ? randInt(1, 9) : randInt(10 ** (d - 1), 10 ** d - 1));
const genSubProblem = (digits) => {
  let a = genNum(digits);
  let b = genNum(digits);
  if (b > a) [a, b] = [b, a];
  return { a, b };
};
const genSet = (n, d) => Array.from({ length: n }, () => genSubProblem(d));

const VerticalProblem = React.memo(({ a, b }) => {
  const aStr = String(a);
  const bStr = String(b);
  const len = Math.max(aStr.length, bStr.length);
  return (
    <pre className="vertical-problem">
      {" " + aStr.padStart(len, " ")}
      {"\n"}
      {"−" + bStr.padStart(len, " ")}
      {"\n"}
      {"-".repeat(len + 1)}
    </pre>
  );
});

const WSCol = ({ a, b }) => {
  const aStr = String(a);
  const bStr = String(b);
  const len = Math.max(aStr.length, bStr.length);
  return (
    <div className="math-col">
      <div className="math-top">{aStr.padStart(len, "\u00a0")}</div>
      <div className="math-bottom">−{bStr.padStart(len, "\u00a0")}</div>
      <div className="math-underline" />
      <div className="math-answer-space">&nbsp;</div>
    </div>
  );
};

async function logSubtractionPractice({ user, digits, numQuestions, correct, attempts, startTime, endTime }) {
  if (!user?.uid) return;
  await setDoc(
    doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
    {
      toolType: TOOL_TYPE,
      topic: `${digits}-digit subtraction`,
      numQuestions,
      correct,
      attempts,
      timestamp: serverTimestamp(),
      startedAt: startTime,
      finishedAt: endTime,
      durationSec: Math.round((endTime - startTime) / 1000),
    },
    { merge: true }
  );
}

export default function MathSubtractionPractice({ onAwardTokens } = {}) {
  const [digits, setDigits] = useState(1);
  const [curr, setCurr] = useState(() => genSubProblem(1));
  const [ans, setAns] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [sheet, setSheet] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const { user } = useAuth();
  const [sessionStart, setSessionStart] = useState(Date.now());
  const audioRef = useRef(null);
  const tokensAwarded = useRef(0);
  const didMountRef = useRef(false);

  // Award token every 10 correct
  useEffect(() => {
    if (correct >= (tokensAwarded.current + 1) * 10) {
      awardToken();
      tokensAwarded.current += 1;
      if (onAwardTokens) onAwardTokens(1);
    }
  }, [correct, onAwardTokens]);

  // Digit change: log session, reset counters, new problem
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      setCurr(genSubProblem(digits));
      setAns("");
      return;
    }
    if (attempts > 0) {
      const endTime = Date.now();
      logSubtractionPractice({
        user,
        digits,
        numQuestions: attempts,
        correct,
        attempts,
        startTime: sessionStart,
        endTime,
      });
    }
    setSessionStart(Date.now());
    setAttempts(0);
    setCorrect(0);
    setCurr(genSubProblem(digits));
    setAns("");
    // eslint-disable-next-line
  }, [digits]);

  
  // Log on unmount
  useEffect(() => {
    return () => {
      if (attempts > 0) {
        const endTime = Date.now();
        logSubtractionPractice({
          user,
          digits,
          numQuestions: attempts,
          correct,
          attempts,
          startTime: sessionStart,
          endTime,
        });
      }
    };
    // eslint-disable-next-line
  }, []);

  // Setup audio
  useEffect(() => {
    const audio = new Audio();
    for (const src of SOUND_SOURCES) {
      const type = src.endsWith(".mp3")
        ? "audio/mpeg"
        : src.endsWith(".ogg")
        ? "audio/ogg"
        : "audio/wav";
      if (audio.canPlayType(type)) {
        audio.src = src;
        break;
      }
    }
    audioRef.current = audio;
  }, []);

  // Handle answer submit (do not reset attempts/correct until log after 10th)
const submit = (e) => {
  e.preventDefault();
  if (!ans.trim() || waitingForNext) return;
  const right = Number(ans) === curr.a - curr.b;

  // Compute new scores:
  const nextAttempts = attempts + 1;
  const nextCorrect = right ? correct + 1 : correct;

  setAttempts(nextAttempts);
  setCorrect(nextCorrect);

  // Always update Firestore after every attempt!
  const endTime = Date.now();
  logSubtractionPractice({
    user,
    digits,
    numQuestions: nextAttempts,
    correct: nextCorrect,
    attempts: nextAttempts,
    startTime: sessionStart,
    endTime,
  });

  if (right) {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    setCurr(genSubProblem(digits));
    setAns("");
    setFeedback(null);
    setWaitingForNext(false);
  } else {
    setFeedback(`❌ The correct answer is: ${curr.a - curr.b}`);
    setWaitingForNext(true);
  }
};


  const startSheet = (n) => {
    setSheet(genSet(n, digits));
    setTimeout(() => window.print(), 100);
  };

  if (sheet) {
    return (
      <div className="worksheet">
        <h2 className="no-print">Subtraction Worksheet – {digits}-digit</h2>
        <button className="no-print" onClick={() => setSheet(null)}>
          Back to Practice
        </button>
        <div className="sheet-grid">
          {sheet.map((p, i) => (
            <WSCol key={i} {...p} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="practice-container">
      <h2>Subtraction Practice</h2>
      <fieldset>
        <legend>Choose number size:</legend>
        {DIGIT_OPTIONS.map(({ label, value }) => (
          <label key={value} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              checked={digits === value}
              onChange={() => setDigits(value)}
            />{" "}
            {label}
          </label>
        ))}
      </fieldset>
      <VerticalProblem a={curr.a} b={curr.b} />
      <form onSubmit={submit} className="answer-form">
        <input
          type="number"
          value={ans}
          onChange={(e) => setAns(e.target.value)}
          autoFocus
          disabled={waitingForNext}
        />
        <button
          type="submit"
          style={{ marginLeft: "0.5rem" }}
          disabled={waitingForNext}
        >
          Submit
        </button>
      </form>
      {feedback && (
        <div className="math-practice-feedback" style={{ marginBottom: 7 }}>
          {feedback}
        </div>
      )}
      {waitingForNext && (
        <button
          className="math-pill-btn"
          style={{ marginTop: "0.7em" }}
          onClick={() => {
            setCurr(genSubProblem(digits));
            setAns("");
            setFeedback(null);
            setWaitingForNext(false);
          }}
        >
          Next
        </button>
      )}

      <p>Score: {correct} / {attempts}</p>
      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map((n) => (
          <button
            key={n}
            onClick={() => startSheet(n)}
            style={{ marginLeft: "0.5rem" }}
          >
            {n} Questions
          </button>
        ))}
      </div>
    </div>
  );
}
